import type { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { TagsUtils } from '@frontend/api-tag';
import { checkAggregateACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Button,
  Checkbox,
  Chip,
  PopoverMenu,
  PopoverMenuItem,
  SearchField,
  Text,
  useFormField,
  usePopoverMenu,
} from '@frontend/design-system';

type SmsTagsFilterPopoverContentProps = {
  tags: Tag[];
  selectTags: (tagIds: string[]) => void;
  selectedTags: string[];
  closeParentMenu: () => void;
  getMenuProps: ReturnType<typeof usePopoverMenu<HTMLButtonElement>>['getMenuProps'];
  getItemProps: ReturnType<typeof usePopoverMenu<HTMLButtonElement>>['getItemProps'];
};

export const TagsSelectPopover = ({
  tags,
  selectTags,
  selectedTags,
  closeParentMenu,
  getMenuProps,
  getItemProps,
}: SmsTagsFilterPopoverContentProps) => {
  const { t } = useTranslation('fax');
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const { selectedLocationIds } = useAppScopeStore();
  const searchFieldProps = useFormField({ type: 'text' });
  const { aggregateValue: hasTagACL } = checkAggregateACL(selectedLocationIds, Permission.TAG_MANAGE, 'any');

  const filteredTags = searchFieldProps.value
    ? tags.filter((tag) => tag.name.toLowerCase().includes(searchFieldProps.value.toLowerCase()))
    : tags;
  const filteredTagsIds = filteredTags.map((tag) => tag.id || tag.smartTagId);

  return (
    <PopoverMenu {...getMenuProps()}>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 0,
          disableCloseOnSelect: true,
        })}
        css={{
          paddingTop: theme.spacing(1),
          maxWidth: 220,
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <SearchField
          {...searchFieldProps}
          name='tag-search'
          onKeyDownCapture={(e) => {
            e.stopPropagation();
          }}
        />
      </PopoverMenuItem>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 1,
          disableCloseOnSelect: true,
        })}
        css={{
          marginTop: theme.spacing(1),
          padding: theme.spacing(1, 0.75),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <Button
          variant='secondary'
          css={{ fontWeight: 'bold', color: theme.colors.primary50, border: 'none' }}
          onClick={() => selectTags(filteredTagsIds)}
        >
          {t('Select all')}
        </Button>
        <Button
          variant='secondary'
          css={{
            fontWeight: 'bold',
            color: theme.colors.primary50,
            border: 'none',
          }}
          onClick={() => {
            selectTags([]);
          }}
          disabled={!selectedTags?.length}
        >
          {t('Clear')}
        </Button>
      </PopoverMenuItem>
      <div css={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 240 }}>
        {filteredTags.map((tag, i) => {
          const tagId = tag.id || tag.smartTagId;
          return (
            <PopoverMenuItem
              key={tagId}
              as='span'
              {...getItemProps({
                index: i + 2,
                disableCloseOnSelect: true,
                onClick: () => {
                  selectTags([tagId]);
                },
              })}
              css={{
                width: '100%',
                height: 'auto',
                padding: theme.spacing(1, 2),
                cursor: 'pointer',
                svg: { fill: theme.colors.white },
              }}
            >
              <Checkbox
                id={tagId}
                value={!!selectedTags?.includes(tagId)}
                onChange={() => {}}
                onFocus={() => {}}
                onBlur={() => {}}
                aria-invalid={false}
                active={false}
                error={false}
                name={tagId}
              />

              <Chip.Tag color={TagsUtils.convertStringToTagColor(tag.color)} css={{ cursor: 'pointer' }}>
                {tag.name}
              </Chip.Tag>
            </PopoverMenuItem>
          );
        })}
      </div>
      {hasTagACL && (
        <PopoverMenuItem
          {...getItemProps({
            index: (filteredTags?.length ?? 0) + 2,
            onClick: () => {
              settingsNavigate({ to: '/tags/quick-create', context: { name: searchFieldProps.value } });
              closeParentMenu();
            },
          })}
          css={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <Icon name='plus-small' color='primary' />
          <Text color='primary' weight='semibold' size='medium'>
            {searchFieldProps.value
              ? t("Create '{{tagName}}' Tag", { tagName: searchFieldProps.value })
              : t('Create Tag')}
          </Text>
        </PopoverMenuItem>
      )}
      <PopoverMenuItem
        {...getItemProps({
          index: (filteredTags?.length ?? 0) + 3,
          onClick: () => {
            settingsNavigate({ to: '/tags' });
            closeParentMenu();
          },
        })}
        css={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <Icon name='settings-small' color='primary' />
        <Text color='primary' weight='semibold' size='medium'>
          {t('Tag Settings')}
        </Text>
      </PopoverMenuItem>
    </PopoverMenu>
  );
};
